import {
  blobToBase64,
  isAcceptableFileUploadContentType,
  isAcceptableImageUploadContentType,
  isAcceptablePdfUploadContentType,
} from "@src/appV2/lib/files";
import { isAcceptableMSWordUploadContentType } from "@src/appV2/lib/files/utils";
import { type UploadFileData, type UploadImageFileData } from "@src/appV2/lib/Media/types";

import { constructPdfFromMultipleImages } from "./constructPdfFromMultipleImages";
import { mergePdfs } from "./mergePdfs";

/*
    We can either upload a single PDF file or a single Image file.
    We allow multiple image inputs, but we combine this into a single pdf before uploading
*/
export async function validateAndGetUploadFile(
  selectedFiles: UploadFileData[]
): Promise<UploadFileData> {
  if (selectedFiles.length === 1) {
    if (!isAcceptableFileUploadContentType(selectedFiles[0].contentType)) {
      throw new Error("Invalid file type selected for upload");
    }

    return selectedFiles[0];
  }

  if (
    selectedFiles.length > 1 &&
    selectedFiles.every((file) => isAcceptableImageUploadContentType(file.contentType))
  ) {
    const blob = await constructPdfFromMultipleImages(selectedFiles as UploadImageFileData[]);
    return {
      dataUrlBase64: await blobToBase64(blob),
      blob,
      extension: "pdf",
      contentType: "application/pdf",
    };
  }

  throw new Error("Invalid Selected Files");
}

/*
    Enhanced file upload validation with PDF merging support.
    Always returns a single PDF file regardless of input.
    Supports:
    - Single or multiple images (converts to single PDF)
    - Single or multiple PDFs (merges into single PDF)
    - Mixed images and PDFs (converts images to PDF, then merges all PDFs)
    
    Validates that all files are acceptable image or PDF types before processing.
*/
export async function validateAndGetUploadFileWithMerging(
  selectedFiles: UploadFileData[]
): Promise<UploadFileData> {
  if (selectedFiles.length === 0) {
    throw new Error("No files selected for upload");
  }

  const allFilesValid = selectedFiles.every(
    (file) =>
      isAcceptableImageUploadContentType(file.contentType) ||
      isAcceptableMSWordUploadContentType(file.contentType) ||
      isAcceptablePdfUploadContentType(file.contentType)
  );

  if (!allFilesValid) {
    throw new Error("Invalid file type selected for upload");
  }

  // Separate images and PDFs
  const imageFiles = selectedFiles.filter((file) =>
    isAcceptableImageUploadContentType(file.contentType)
  ) as UploadImageFileData[];
  const pdfFiles = selectedFiles.filter((file) =>
    isAcceptablePdfUploadContentType(file.contentType)
  );

  const pdfsToMerge: Array<{
    dataUrlBase64: string;
    contentType: "application/pdf";
    extension: string;
  }> = [];

  // Convert images to PDF if any exist
  if (imageFiles.length > 0) {
    const imagesPdfBlob = await constructPdfFromMultipleImages(imageFiles);
    pdfsToMerge.push({
      dataUrlBase64: await blobToBase64(imagesPdfBlob),
      contentType: "application/pdf",
      extension: "pdf",
    });
  }

  // Add existing PDFs to merge list
  pdfsToMerge.push(
    ...pdfFiles.map((file) => ({
      dataUrlBase64: file.dataUrlBase64,
      contentType: "application/pdf" as const,
      extension: file.extension,
    }))
  );

  // Merge all PDFs into one
  const finalBlob = await mergePdfs(pdfsToMerge);
  return {
    dataUrlBase64: await blobToBase64(finalBlob),
    blob: finalBlob,
    extension: "pdf",
    contentType: "application/pdf",
  };
}
